import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Ban, RotateCcw, Users } from "lucide-react";
import { createClosure, reopenClosure, updateShelter, formatApiError } from "@/lib/api";

const REASONS = ["flooded", "debris", "landslide", "collapsed", "downed lines", "other"];

export default function AssetActionCard({ asset, session, closure, shelter, onClose, onChanged, onRequireLogin, rightOffset = 0 }) {
  const [reason, setReason] = useState("flooded");
  const [busy, setBusy] = useState(false);
  const [occ, setOcc] = useState("");
  const [cap, setCap] = useState("");
  useEffect(() => { setOcc(shelter?.occupancy ?? ""); setCap(shelter?.capacity ?? ""); }, [shelter]);
  if (!asset) return null;
  const isLink = ["road", "bridge", "port"].includes(asset.asset_type);

  const run = async (fn, ok) => {
    if (!session) return onRequireLogin();
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="asset-action-card" style={{ right: 64 + rightOffset }} className="absolute bottom-3 z-[600] transition-[right] duration-300 glass rounded-lg p-3 w-[290px] border border-border flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div data-testid="asset-action-name" className="font-heading font-bold uppercase text-sm text-slate-100 truncate">{asset.name}</div>
          <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-500">{asset.asset_type} · {asset.parish || asset.category || ""}</div>
        </div>
        <button data-testid="asset-action-close" onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-4 h-4" /></button>
      </div>

      {isLink && (closure ? (
        <div className="flex flex-col gap-2">
          <div data-testid="asset-closure-status" className="font-mono-t text-[10px] text-risk-critical">
            IMPASSABLE · {closure.reason} · by {closure.reported_by} · {new Date(closure.created_at).toLocaleTimeString()}
          </div>
          <button data-testid="asset-reopen-button" disabled={busy} onClick={() => run(() => reopenClosure(closure.id), "Reopened — isolation model updated")}
            className="flex items-center justify-center gap-1.5 border border-emerald-500/60 text-emerald-300 font-heading font-semibold uppercase text-xs py-1.5 rounded-md hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
            <RotateCcw className="w-3.5 h-3.5" /> Mark reopened
          </button>
        </div>
      ) : (
        <div className="flex gap-1.5">
          <select data-testid="asset-closure-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            className="flex-1 bg-slate-900/70 border border-border rounded-md px-2 text-xs font-mono-t text-slate-200">
            {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="asset-close-button" disabled={busy} onClick={() => run(() => createClosure(asset.id, reason, ""), "Marked impassable — isolation model updated")}
            className="flex items-center gap-1.5 bg-risk-critical text-white font-heading font-semibold uppercase text-xs px-3 py-1.5 rounded-md hover:bg-risk-critical/85 transition-colors disabled:opacity-50">
            <Ban className="w-3.5 h-3.5" /> Impassable
          </button>
        </div>
      ))}

      {asset.asset_type === "shelter" && shelter && (shelter.role === "public_shelter" ? (
        <div className="flex flex-col gap-1.5">
          <div className="font-mono-t text-[10px] text-slate-400 flex items-center gap-1">
            <Users className="w-3 h-3" /> {shelter.occupancy}/{shelter.capacity} in · demand {shelter.predicted_demand} · {shelter.status.toUpperCase()}
          </div>
          <div className="font-mono-t text-[9px] text-slate-500">
            Capacity source: {shelter.capacity_source === "staff" ? `staff (${shelter.updated_by})` : shelter.capacity_source === "osm_footprint" ? `${shelter.floor_m2} m² floor area` : "category default"}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <label className="font-mono-t text-[9px] uppercase text-slate-500">Checked in
              <input data-testid="shelter-occupancy-input" type="number" min="0" value={occ} onChange={(e) => setOcc(e.target.value)}
                className="w-full mt-0.5 bg-slate-900/70 border border-border rounded px-2 py-1 text-xs text-slate-100" />
            </label>
            <label className="font-mono-t text-[9px] uppercase text-slate-500">Capacity
              <input data-testid="shelter-capacity-input" type="number" min="0" value={cap} onChange={(e) => setCap(e.target.value)}
                className="w-full mt-0.5 bg-slate-900/70 border border-border rounded px-2 py-1 text-xs text-slate-100" />
            </label>
          </div>
          <button data-testid="shelter-save-button" disabled={busy} onClick={() => run(() => updateShelter(asset.id, {
            occupancy: occ === "" ? null : Number(occ),
            capacity: cap === "" || Number(cap) === shelter.capacity ? null : Number(cap) }), "Shelter updated")}
            className="bg-cyan text-slate-950 font-heading font-semibold uppercase text-xs py-1.5 rounded-md hover:bg-cyan/85 transition-colors disabled:opacity-50">
            Save head-count
          </button>
        </div>
      ) : (
        <div className="font-mono-t text-[10px] text-slate-500">Critical facility ({shelter.category}) — not a public hurricane shelter.</div>
      ))}
      {!session && <div className="font-mono-t text-[9px] text-slate-500">Field sign-in required to report.</div>}
    </div>
  );
}
